import type { Column, ColumnKind } from '../../../modelos/precios/PriceTypes';
import type { GlobalRuleType } from '../../../modelos/precios/PriceTypes';

export const BASE_COLUMN_ID = 'P1';
export const MIN_ALLOWED_COLUMN_ID = 'P2';
export const WHOLESALE_COLUMN_ID = 'P3';
export const DISTRIBUTOR_COLUMN_ID = 'P4';
export const CORPORATE_COLUMN_ID = 'P5';
export const PREFERRED_COLUMN_ID = 'P6';
export const PROMOTIONAL_COLUMN_ID = 'P7';
export const GLOBAL_DISCOUNT_COLUMN_ID = 'P8';
export const GLOBAL_INCREASE_COLUMN_ID = 'P9';
export const OTROS_1_COLUMN_ID = 'P10';
export const OTROS_2_COLUMN_ID = 'P11';
export const OTROS_3_COLUMN_ID = 'P12';
const MANUAL_COLUMN_ID_PATTERN = /^P(\d+)$/i;

type FixedColumnDefinition = {
  id: string;
  legacyIds?: string[];
  order: number;
  defaultName: string;
  kind: ColumnKind;
  isBase: boolean;
  defaultVisible?: boolean;
  defaultVisibleInTable?: boolean;
  defaultMode?: Column['mode'];
  helpText?: string;
  defaultUsarEnPuntoVenta?: boolean;
  defaultUsarEnComprobantes?: boolean;
};

const FIXED_COLUMN_DEFINITIONS: FixedColumnDefinition[] = [
  {
    id: BASE_COLUMN_ID,
    order: 1,
    defaultName: 'PRECIO BASE',
    kind: 'base',
    isBase: true,
    defaultVisible: true,
    defaultVisibleInTable: true,
    defaultMode: 'fixed',
    helpText: 'Precio de referencia principal definido manualmente.',
    defaultUsarEnPuntoVenta: true,
    defaultUsarEnComprobantes: true,
  },
  {
    id: MIN_ALLOWED_COLUMN_ID,
    legacyIds: ['PPM'],
    order: 2,
    defaultName: 'PRECIO MÍNIMO',
    kind: 'min-allowed',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Valor mínimo permitido para aplicar descuentos.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: WHOLESALE_COLUMN_ID,
    legacyIds: ['PPD'],
    order: 3,
    defaultName: 'PRECIO MAYORISTA',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio manual para ventas al por mayor.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: DISTRIBUTOR_COLUMN_ID,
    order: 4,
    defaultName: 'PRECIO DISTRIBUIDOR',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Lista especial para socios distribuidores.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: CORPORATE_COLUMN_ID,
    order: 5,
    defaultName: 'PRECIO CORPORATIVO',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Tarifa fija para cuentas corporativas.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: PREFERRED_COLUMN_ID,
    legacyIds: ['P5'],
    order: 6,
    defaultName: 'PRECIO PREFERENCIAL',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio exclusivo para clientes frecuentes.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: PROMOTIONAL_COLUMN_ID,
    legacyIds: ['P6'],
    order: 7,
    defaultName: 'PRECIO PROMOCIONAL',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio especial para campañas y ofertas.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: GLOBAL_DISCOUNT_COLUMN_ID,
    legacyIds: ['P7', 'PGD'],
    order: 8,
    defaultName: 'DESCUENTO GLOBAL',
    kind: 'global-discount',
    isBase: false,
    defaultVisible: true,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Aplicación automática de un descuento sobre el precio base.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: GLOBAL_INCREASE_COLUMN_ID,
    legacyIds: ['P8', 'PGA'],
    order: 9,
    defaultName: 'AUMENTO GLOBAL',
    kind: 'global-increase',
    isBase: false,
    defaultVisible: true,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Aplicación automática de un recargo sobre el precio base.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: OTROS_1_COLUMN_ID,
    order: 10,
    defaultName: 'OTROS 1',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio manual personalizable.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: OTROS_2_COLUMN_ID,
    order: 11,
    defaultName: 'OTROS 2',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio manual personalizable.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
  {
    id: OTROS_3_COLUMN_ID,
    order: 12,
    defaultName: 'OTROS 3',
    kind: 'manual',
    isBase: false,
    defaultVisible: false,
    defaultVisibleInTable: false,
    defaultMode: 'fixed',
    helpText: 'Precio manual personalizable.',
    defaultUsarEnPuntoVenta: false,
    defaultUsarEnComprobantes: false,
  },
];

const fixedColumnMap = new Map(FIXED_COLUMN_DEFINITIONS.map(def => [def.id, def] as const));
const legacyColumnIdMap = new Map<string, string>(
  FIXED_COLUMN_DEFINITIONS.flatMap(def => (def.legacyIds ?? []).map(legacyId => [legacyId, def.id] as const))
);

const getCanonicalFixedColumnId = (columnId?: string): string | undefined => {
  if (!columnId) return undefined;
  if (fixedColumnMap.has(columnId)) {
    return columnId;
  }
  return legacyColumnIdMap.get(columnId);
};

const normalizeGlobalRuleValue = (value?: number | null): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) {
    return null;
  }
  return value;
};

const buildFixedColumn = (definition: FixedColumnDefinition, existing?: Column): Column => {
  const resolvedOrder = typeof existing?.order === 'number' && Number.isFinite(existing.order)
    ? existing.order
    : definition.order;
  const column: Column = {
    id: definition.id,
    name: existing?.name ?? definition.defaultName,
    mode: definition.defaultMode ?? 'fixed',
    visible: typeof existing?.visible === 'boolean' ? existing.visible : definition.defaultVisible !== false,
    isVisibleInTable: typeof existing?.isVisibleInTable === 'boolean'
      ? existing.isVisibleInTable
      : definition.defaultVisibleInTable !== false,
    isBase: definition.isBase,
    order: resolvedOrder,
    kind: definition.kind
  };

  if (definition.isBase) {
    column.isVisibleInTable = true;
  }

  if (definition.kind === 'global-discount' || definition.kind === 'global-increase') {
    column.globalRuleType = existing?.globalRuleType ?? 'percent';
    column.globalRuleValue = normalizeGlobalRuleValue(existing?.globalRuleValue);
  }

  // Disponibilidad por canal: respeta valores guardados; globals no tienen opciones de canal
  if (definition.kind !== 'global-discount' && definition.kind !== 'global-increase') {
    column.usarEnPuntoVenta = existing?.usarEnPuntoVenta ?? definition.defaultUsarEnPuntoVenta ?? false;
    column.usarEnComprobantes = existing?.usarEnComprobantes ?? definition.defaultUsarEnComprobantes ?? false;
  }

  return column;
};

const normalizeColumnOrder = (column: Column, index: number): Column => ({
  ...column,
  order: typeof column.order === 'number' ? column.order : index + 1
});

const sanitizeColumn = (column: Column, index: number): Column => {
  const requiresReindex = column.id === GLOBAL_INCREASE_COLUMN_ID && column.kind !== 'global-increase';
  const workingColumn = requiresReindex ? { ...column, id: undefined } : column;

  const canonicalId = getCanonicalFixedColumnId(workingColumn.id);
  if (canonicalId) {
    const definition = fixedColumnMap.get(canonicalId)!;
    return buildFixedColumn(definition, { ...workingColumn, id: canonicalId });
  }

  const derivedKind: ColumnKind = workingColumn.kind && workingColumn.kind !== 'base'
    ? workingColumn.kind
    : 'manual';

  const normalized: Column = {
    ...workingColumn,
    id: workingColumn.id || `P${index + FIXED_COLUMN_DEFINITIONS.length + 1}`,
    order: typeof workingColumn.order === 'number' && Number.isFinite(workingColumn.order) ? workingColumn.order : index + 1,
    kind: derivedKind,
    isBase: false,
    mode: derivedKind === 'manual' && workingColumn.mode === 'volume' ? 'volume' : 'fixed',
    visible: workingColumn.visible !== false,
    isVisibleInTable: workingColumn.isVisibleInTable !== false
  };

  if (derivedKind === 'global-discount' || derivedKind === 'global-increase') {
    normalized.globalRuleType = workingColumn.globalRuleType ?? 'percent';
    normalized.globalRuleValue = normalizeGlobalRuleValue(workingColumn.globalRuleValue);
  } else {
    delete normalized.globalRuleType;
    delete normalized.globalRuleValue;
  }

  return normalized;
};

export const generateColumnId = (columns: Column[]): string => {
  const numericSuffixes = columns
    .map(column => {
      const match = MANUAL_COLUMN_ID_PATTERN.exec(column.id || '');
      return match ? Number(match[1]) : null;
    })
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  const nextValue = numericSuffixes.length > 0
    ? Math.max(...numericSuffixes) + 1
    : 1;

  return `P${nextValue}`;
};

export const getNextOrder = (columns: Column[]): number => {
  if (columns.length === 0) return 1;
  return Math.max(...columns.map(c => c.order)) + 1;
};

export const getFixedColumnDefinition = (columnId?: string): FixedColumnDefinition | undefined => {
  const canonicalId = getCanonicalFixedColumnId(columnId);
  if (!canonicalId) return undefined;
  return fixedColumnMap.get(canonicalId);
};

export const isFixedColumnId = (columnId?: string): boolean => Boolean(getFixedColumnDefinition(columnId));
export const isFixedColumn = (column: Column): boolean => isFixedColumnId(column.id);

export const getCanonicalColumnId = (columnId: string): string => {
  return getCanonicalFixedColumnId(columnId) ?? columnId;
};

export const getFixedColumnHelpText = (columnId: string): string | undefined => {
  return getFixedColumnDefinition(columnId)?.helpText;
};

export const getDefaultTableVisibility = (columnId: string): boolean => {
  const definition = getFixedColumnDefinition(columnId);
  if (!definition) {
    return true;
  }
  return definition.defaultVisibleInTable !== false;
};

export const getDefaultColumnOrder = (columnId: string): number | undefined => {
  return getFixedColumnDefinition(columnId)?.order;
};

export const getColumnDisplayName = (column: Column): string => column.name;

export const ensureRequiredColumns = (columns: Column[]): Column[] => {
  const normalized = columns
    .filter(Boolean)
    .map((column, index) => sanitizeColumn(normalizeColumnOrder(column, index), index));

  const fixedColumns = FIXED_COLUMN_DEFINITIONS.map(definition => {
    const existing = normalized.find(column => column.id === definition.id);
    return buildFixedColumn(definition, existing);
  });

  const manualColumns: Column[] = normalized
    .filter(column => !isFixedColumn(column))
    .sort((a, b) => a.order - b.order)
    .map(column => ({
      ...column,
      kind: 'manual',
      isBase: false,
      visible: column.visible !== false,
      isVisibleInTable: column.isVisibleInTable !== false,
      mode: column.mode === 'volume' ? 'volume' : 'fixed'
    }));

  const combined = [...fixedColumns, ...manualColumns];
  combined.sort((a, b) => {
    if (a.order === b.order) {
      return a.id.localeCompare(b.id);
    }
    return a.order - b.order;
  });

  return combined.map((column, index) => ({
    ...column,
    order: index + 1
  }));
};

export const filterVisibleColumns = (columns: Column[]): Column[] => {
  return columns.filter(col => col.visible).sort((a, b) => a.order - b.order);
};

export const findBaseColumn = (columns: Column[]): Column | undefined => {
  return columns.find(col => col.kind === 'base');
};

export const isGlobalColumn = (column: Column): boolean => {
  return column.kind === 'global-discount' || column.kind === 'global-increase';
};

export const isProductDiscountColumn = (column: Column): boolean => column.kind === 'product-discount';
export const isMinAllowedColumn = (column: Column): boolean => column.kind === 'min-allowed';
export const isManualInputColumn = (column: Column): boolean =>
  column.kind === 'manual' || isProductDiscountColumn(column) || isMinAllowedColumn(column);

export const validateColumnConfiguration = (columns: Column[]): {
  hasBase: boolean;
  hasVisible: boolean;
  isValid: boolean;
} => {
  const hasBase = columns.some(c => c.kind === 'base');
  const hasVisible = columns.some(c => c.visible);
  const hasVisibleEditable = columns.some(c => c.visible && (c.kind === 'base' || c.kind === 'manual' || c.kind === 'product-discount' || c.kind === 'min-allowed'));
  const isValid = hasBase && hasVisible && hasVisibleEditable;

  return { hasBase, hasVisible, isValid };
};

export const applyGlobalRule = (column: Column, baseValue: number): number | null => {
  if (!isGlobalColumn(column)) {
    return null;
  }

  const ruleValue = typeof column.globalRuleValue === 'number' ? column.globalRuleValue : null;
  if (ruleValue === null) {
    return null;
  }

  const ruleType: GlobalRuleType = column.globalRuleType ?? 'percent';
  if (ruleType === 'percent') {
    const modifier = ruleValue / 100;
    const computed = column.kind === 'global-discount'
      ? baseValue * (1 - modifier)
      : baseValue * (1 + modifier);
    return Math.max(computed, 0);
  }

  return Math.max(column.kind === 'global-discount' ? baseValue - ruleValue : baseValue + ruleValue, 0);
};
